<script setup lang="ts">
import { useI18n } from '#i18n';
import { XModal, XIcon } from '@clicksign/excalibur';
import { useForm } from 'vee-validate';
import { z } from 'zod';
import { toTypedSchema } from '@vee-validate/zod';
import { useToast } from 'vue-toastification';
import { syncRefs } from '@vueuse/core';

import { AppTextInput, BrandButton } from '~~base-ui';
import { isFetchError, useCurrentSignatureRequestKey, urlToBlob } from '~~core';
import { SignaturePad } from '~~requirement';

import { saveRubricates } from '../../services/document';

export type PositionSignModalEmits = {
  (event: 'next', override: boolean): void;
  (event: 'close'): void;
};

export type Form = {
  initials?: string | null;
  manuscript?: Blob | null;
};

export type ArtefactType = {
  manuscript?: string | null;
  initials?: string | null;
};

export type PositionSignModalProps = {
  isModalOpen: boolean,
  positionedKinds: Array<keyof Form>,
  artefacts: ArtefactType,
};

function isBlobImage(blob: Blob) {
  return blob.type.includes('image');
}

const formSchema = z.object({
  initials: z.string().min(2).max(2),
});

const { t } = useI18n();
const toast = useToast();
const gtm = useGtm();

const emit = defineEmits<PositionSignModalEmits>();

const props = defineProps<PositionSignModalProps>();

const { signatureRequestKey } = useCurrentSignatureRequestKey();

const canShowInitialsField = computed(() => props.positionedKinds.includes('initials'));
const canShowManuscriptField = computed(() => props.positionedKinds.includes('manuscript'));

const fields = computed<Form>(() => (
  Object.fromEntries(props.positionedKinds.map((field) => [field, '']))
));

useForm<z.infer<typeof formSchema>>({
  validationSchema: toTypedSchema(formSchema as any), // Extensive typing issue with vee-validate + zod
});

const form = ref<Form>(fields.value);

syncRefs(fields, form);

const shouldDisableSaveButton = computed(() => props.positionedKinds.some((field) => !form.value[field]));

const imgUrl = computed(() => props.artefacts.manuscript || undefined);

const manuscriptBlob = ref<Blob | undefined>();

onMounted(async () => {
  const { initials, manuscript } = props.artefacts;

  if (canShowInitialsField.value) {
    form.value.initials = initials || '';
  }

  if (canShowManuscriptField.value && manuscript) {
    const blob = await urlToBlob(manuscript);

    if (!isBlobImage(blob)) return;

    manuscriptBlob.value = blob;
    form.value.manuscript = blob;
  }
});

function handleSignaturePadChange(blob: Blob | undefined) {
  form.value.manuscript = blob;
}

const isLoading = ref(false);

const next = async () => {
  isLoading.value = true;

  const hasSameInitials = !canShowInitialsField.value || (form.value.initials === props.artefacts.initials);
  const hasSameManuscript = !canShowManuscriptField.value || (form.value.manuscript === manuscriptBlob.value);

  const isReusedArtefact = hasSameInitials && hasSameManuscript;

  if (isReusedArtefact) {
    emit('next', isReusedArtefact);

    return;
  }

  try {
    await saveRubricates(signatureRequestKey.value, { rubricate: form.value });
    gtm?.trackEvent({
      event: 'ga-send',
      ga_event_category: 'positioned-sign-signer',
      ga_event_name: 'button-modal-create-sign',
    });

    emit('next', isReusedArtefact);
  } catch (err) {
    toast.error(t('error.unexpected'));

    if (!isFetchError(err)) throw err;
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <XModal
    :title="t('document.rubricate.signatureModal.modalTitle')"
    :is-open="isModalOpen"
    data-testid="positionSignModal"
    @close="$emit('close')"
  >
    <AppTextInput
      v-if="canShowInitialsField"
      v-model="form.initials"
      :class="$style.initials"
      name="initials"
      :mask="{mask: 'AA'}"
      :label="t('document.rubricate.signatureModal.rubric')"
      :placeholder="'DS'"
      required
      data-testid="initialsField"
    />

    <template v-if="canShowManuscriptField">
      <SignaturePad
        :class="$style.pad"
        :img-url="imgUrl"
        width="100%"
        height="100%"
        data-testid="canvasContainer"
        @change="handleSignaturePadChange"
      />

      <small :class="$style.disclaimer">
        <XIcon
          icon="info-circle"
          size="lg"
          left
        />
        {{ t('document.rubricate.signatureModal.disclaimer') }}
      </small>
    </template>

    <template #actions>
      <BrandButton
        design="outlined"
        data-testid="closeButton"
        @click="emit('close')"
      >
        {{ t('common.close') }}
      </BrandButton>

      <BrandButton
        :disabled="shouldDisableSaveButton"
        data-testid="nextButton"
        :loading="isLoading"
        @click="next"
      >
        {{ t('document.rubricate.signatureModal.forwardToApply') }}
      </BrandButton>
    </template>
  </XModal>
</template>

<style lang="scss" module>
.container {
  display: flex;
  flex-direction: column;
}

.pad {
  width: 100%;
  background-color: var(--color-neutral-0);
}

.actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-small-x) var(--space-small-xx);
}

.header {
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon {
  color: var(--color-brand-primary-400);
}

.disclaimer {
  display: block;
  margin-top: var(--space-small-xx);
  font-weight: var(--font-weight-semibold);
  color: var(--color-neutral-800);
}

.initials {
  margin-bottom: var(--space-regular);

  input {
    text-transform: uppercase;
  }
}
</style>
